import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Alert,
} from '@mui/material';
import {
  ShoppingCartCheckout as CartIcon,
  TrendingDown as AbandonIcon,
  AttachMoney as MoneyIcon,
  RestartAlt as RecoverIcon,
  Email as EmailIcon,
  TouchApp as ClickIcon,
  CheckCircleOutline as RecoveredIcon,
  PaidOutlined as RevenueIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import api from '../../services/api';
import {
  StatCard,
  SectionLoading,
  SectionError,
  COLORS,
  formatNumber,
  formatCurrency,
  formatPct,
  formatDate,
} from './shared';

interface AbandonedData {
  days: number;
  total_started: number;
  total_abandoned: number;
  total_recoverable: number;
  abandon_rate: number;
  total_abandoned_value: number;
  avg_abandoned_value: number;
  daily: {
    date: string;
    started: number;
    abandoned: number;
    abandoned_value: number;
    recoverable: number;
  }[];
  top_products: {
    product_id: string;
    product_title: string;
    image_url?: string;
    abandoned_qty: number;
    abandoned_sessions: number;
    abandoned_value: number;
  }[];
  hourly: { hour: number; abandoned: number }[];
}

interface RecoveryStats {
  days: number;
  sent_today: number;
  sent_yesterday: number;
  sent_last_7d: number;
  sent_all_time: number;
  clicked_all_time: number;
  last_sent_at: string | null;
  sent_in_window: number;
  clicked_in_window: number;
  ctr_pct: number;
  recovered_orders_in_window: number;
  recovered_revenue_in_window: number;
  recovery_rate_pct: number;
  avg_recovered_order_value: number;
  daily: {
    date: string;
    sent: number;
    clicked: number;
    recovered_orders: number;
    recovered_revenue: number;
  }[];
}

const AbandonedCartSection: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [days, setDays] = useState<number>(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AbandonedData | null>(null);
  const [recovery, setRecovery] = useState<RecoveryStats | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get(`/api/retention/abandoned-cart?days=${days}`),
      api.get(`/api/retention/cart-recovery-stats?days=${days}`),
    ])
      .then(([abandonedRes, recoveryRes]) => {
        setData(abandonedRes.data);
        setRecovery(recoveryRes.data);
      })
      .catch((err) => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [days, refreshKey]);

  if (loading) return <SectionLoading />;
  if (error) return <SectionError message={error} />;
  if (!data) return <SectionError />;

  const recoverablePct =
    data.total_abandoned > 0
      ? +((data.total_recoverable / data.total_abandoned) * 100).toFixed(1)
      : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <MenuItem value={7}>過去 7 天</MenuItem>
            <MenuItem value={30}>過去 30 天</MenuItem>
            <MenuItem value={90}>過去 90 天</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
        棄單資料來自 <code>abandoned_checkouts</code>（結帳表單即時快照）。召回信於棄單 24–72h 自動寄送（每張棄單最多一封）。
        可回收 = 有留下 email 或電話的棄單。
      </Alert>

      {/* Cart recovery email performance */}
      {recovery && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            召回信成效
            <Typography component="span" variant="caption" sx={{ ml: 1, color: COLORS.slate }}>
              累計寄出 {formatNumber(recovery.sent_all_time)} 封 · 最後寄出 {recovery.last_sent_at ? formatDate(recovery.last_sent_at) : '—'}
            </Typography>
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <StatCard
                label="今日寄出"
                value={formatNumber(recovery.sent_today)}
                subtitle={`昨日 ${formatNumber(recovery.sent_yesterday)}・近 7 天 ${formatNumber(recovery.sent_last_7d)}`}
                icon={<EmailIcon />}
                color={COLORS.primary}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label={`期間寄出（${recovery.days} 天）`}
                value={formatNumber(recovery.sent_in_window)}
                subtitle={`點擊 ${formatNumber(recovery.clicked_in_window)} 次`}
                icon={<EmailIcon />}
                color={COLORS.purple}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="點擊率 (CTR)"
                value={formatPct(recovery.ctr_pct)}
                subtitle="點開恢復連結 / 寄出"
                icon={<ClickIcon />}
                color={COLORS.warning}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard
                label="救回營收"
                value={formatCurrency(recovery.recovered_revenue_in_window)}
                subtitle={`救回 ${formatNumber(recovery.recovered_orders_in_window)} 單・回收率 ${formatPct(recovery.recovery_rate_pct)}`}
                icon={<RevenueIcon />}
                color={COLORS.success}
              />
            </Grid>
          </Grid>

          {/* Daily send / click / recovery trend */}
          <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}` }}>
            <CardHeader
              title="召回信每日成效"
              titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
              subheader="寄出 vs 點擊 vs 救回訂單（14 天內購買且 email 相符歸因）"
              subheaderTypographyProps={{ fontSize: '0.75rem' }}
            />
            <CardContent sx={{ pt: 0 }}>
              {recovery.daily.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  期間內尚未寄出召回信
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={recovery.daily.map((d) => ({ ...d, dateLabel: formatDate(d.date) }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="dateLabel" fontSize={11} />
                    <YAxis yAxisId="left" fontSize={11} />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      fontSize={11}
                      tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`}
                    />
                    <Tooltip
                      formatter={(v: any, name: string) =>
                        name === '救回營收' ? formatCurrency(v) : formatNumber(v)
                      }
                    />
                    <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="sent"
                      name="寄出"
                      stroke={COLORS.primary}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="clicked"
                      name="點擊"
                      stroke={COLORS.warning}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="recovered_orders"
                      name="救回訂單"
                      stroke={COLORS.success}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="recovered_revenue"
                      name="救回營收"
                      stroke={COLORS.purple}
                      strokeWidth={1.5}
                      strokeDasharray="4 4"
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1 }}>
        棄單概況
      </Typography>

      {/* Headline */}
      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="棄單數"
            value={formatNumber(data.total_abandoned)}
            subtitle={`結帳發起 ${formatNumber(data.total_started)} 次`}
            icon={<AbandonIcon />}
            color={COLORS.danger}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="棄單率"
            value={formatPct(data.abandon_rate)}
            subtitle="未於 6 小時內完成付款"
            icon={<CartIcon />}
            color={COLORS.warning}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="棄單總金額"
            value={formatCurrency(data.total_abandoned_value)}
            subtitle={`平均每筆 ${formatCurrency(data.avg_abandoned_value)}`}
            icon={<MoneyIcon />}
            color={COLORS.purple}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="可回收棄單"
            value={formatNumber(data.total_recoverable)}
            subtitle={`${formatPct(recoverablePct)}（有 email／電話）`}
            icon={<RecoverIcon />}
            color={COLORS.success}
          />
        </Grid>
      </Grid>

      {/* Daily trend */}
      <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}` }}>
        <CardHeader
          title="每日棄單趨勢"
          titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
        />
        <CardContent sx={{ pt: 0 }}>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.daily.map((d) => ({ ...d, dateLabel: formatDate(d.date) }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="dateLabel" fontSize={11} />
              <YAxis yAxisId="left" fontSize={11} />
              <YAxis
                yAxisId="right"
                orientation="right"
                fontSize={11}
                tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`}
              />
              <Tooltip
                formatter={(v: any, name: string) => (name === '棄單金額' ? formatCurrency(v) : formatNumber(v))}
              />
              <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="abandoned"
                name="棄單數"
                stroke={COLORS.danger}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="recoverable"
                name="可回收"
                stroke={COLORS.success}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="abandoned_value"
                name="棄單金額"
                stroke={COLORS.purple}
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {/* Hourly pattern */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}`, height: '100%' }}>
            <CardHeader
              title="棄單時段分佈"
              titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
              subheader="24 小時棄單頻率 — 決定寄信／LINE 召回的最佳時機"
              subheaderTypographyProps={{ fontSize: '0.75rem' }}
            />
            <CardContent sx={{ pt: 0 }}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={Array.from({ length: 24 }, (_, h) => {
                    const found = data.hourly.find((x) => x.hour === h);
                    return { hour: `${h}`, abandoned: found?.abandoned || 0 };
                  })}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="hour" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="abandoned" fill={COLORS.warning} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top abandoned products */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ border: `1px solid ${COLORS.border}`, height: '100%' }}>
            <CardHeader
              title="最常被棄單的商品"
              titleTypographyProps={{ fontSize: '0.95rem', fontWeight: 600 }}
              subheader={`Top 15 — 這些商品最容易進購物車但沒買單`}
              subheaderTypographyProps={{ fontSize: '0.75rem' }}
            />
            <CardContent sx={{ pt: 0 }}>
              {data.top_products.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  目前無棄單商品
                </Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 320 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>商品</TableCell>
                        <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                          次數
                        </TableCell>
                        <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                          數量
                        </TableCell>
                        <TableCell align="right" sx={{ bgcolor: COLORS.bgAlt, fontWeight: 600 }}>
                          金額
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.top_products.map((p, i) => (
                        <TableRow key={`${p.product_id || ''}-${i}`} hover>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {p.image_url ? (
                                <Avatar
                                  src={p.image_url}
                                  variant="rounded"
                                  sx={{ width: 32, height: 32 }}
                                />
                              ) : (
                                <Avatar variant="rounded" sx={{ width: 32, height: 32, bgcolor: '#E2E8F0' }}>
                                  <CartIcon fontSize="small" />
                                </Avatar>
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.8rem',
                                }}
                              >
                                {p.product_title || '(未知)'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatNumber(p.abandoned_sessions)}</TableCell>
                          <TableCell align="right">{formatNumber(p.abandoned_qty)}</TableCell>
                          <TableCell align="right">{formatCurrency(p.abandoned_value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AbandonedCartSection;
